import _classPrivateFieldLooseBase from "@babel/runtime/helpers/esm/classPrivateFieldLooseBase";
import _classPrivateFieldLooseKey from "@babel/runtime/helpers/esm/classPrivateFieldLooseKey";
import { useEffect, useMemo } from 'react';
import { DelayedIntervalScheduler } from '../../../common/scheduler/delayed-interval.scheduler';
import { IdleTaskScheduler } from '../../../common/scheduler/idle-task.scheduler';
import { ImmediateTaskScheduler } from '../../../common/scheduler/immediate-task.scheduler';

// This is to delay the scheduler for the app to be ready - it'll get replaced by waiting for app's rhumb success event in the future
const deferInitially = () => new Promise(r => setTimeout(r, 1000));
var _instance = /*#__PURE__*/_classPrivateFieldLooseKey("instance");
var _scheduler = /*#__PURE__*/_classPrivateFieldLooseKey("scheduler");
/**
 * Singleton class that manages the components scheduler instance
 * Ensures only one scheduler is created per app instance
 */
export class ComponentsSchedulerSingleton {
  constructor() {
    Object.defineProperty(this, _scheduler, {
      writable: true,
      value: null
    });
  }
  static getInstance() {
    if (!_classPrivateFieldLooseBase(ComponentsSchedulerSingleton, _instance)[_instance]) {
      _classPrivateFieldLooseBase(ComponentsSchedulerSingleton, _instance)[_instance] = new ComponentsSchedulerSingleton();
    }
    return _classPrivateFieldLooseBase(ComponentsSchedulerSingleton, _instance)[_instance];
  }

  /**
   * Reset the singleton for testing purposes
   */
  static resetForTests() {
    _classPrivateFieldLooseBase(ComponentsSchedulerSingleton, _instance)[_instance] = null;
  }
  getScheduler(options = {}) {
    if (!_classPrivateFieldLooseBase(this, _scheduler)[_scheduler]) {
      _classPrivateFieldLooseBase(this, _scheduler)[_scheduler] = this.createScheduler(options);
    }
    return _classPrivateFieldLooseBase(this, _scheduler)[_scheduler];
  }
  createScheduler(options) {
    const {
      useDeferInitially = true
    } = options;
    const initialPrecondition = useDeferInitially ? deferInitially : undefined;
    const schedulerOptions = {
      initialPrecondition
    };
    if (IdleTaskScheduler.isSupported()) {
      // Schedule components to load when the browser is idle
      return new IdleTaskScheduler(schedulerOptions);
    }
    if ('jasmine' in window) {
      // Schedule components to load immediately in tests
      return new ImmediateTaskScheduler(schedulerOptions);
    }

    // Schedule components to load with a delay in older browsers
    return new DelayedIntervalScheduler(schedulerOptions);
  }
}
Object.defineProperty(ComponentsSchedulerSingleton, _instance, {
  writable: true,
  value: null
});
export const useComponentsScheduler = (options = {}) => {
  // Get the singleton scheduler instance
  const scheduler = useMemo(() => ComponentsSchedulerSingleton.getInstance().getScheduler(options), [options]);
  useEffect(() => {
    scheduler.run();
    return () => {
      scheduler.cancel();
    };
  }, [scheduler]);
  return scheduler;
};